import React, { useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Users, Shield, TrendingUp, Award, CheckCircle, Plus } from 'lucide-react';

export default function EnterprisePage() {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const packages = [
    {
      name: 'Starter Team',
      quantity: '10-50 Cards',
      price: '$39',
      priceNote: 'per card',
      features: [
        'Bulk discount pricing',
        'Team dashboard access',
        'Basic customization',
        'Standard delivery',
        'Email support',
      ],
    },
    {
      name: 'Growing Business',
      quantity: '51-200 Cards',
      price: '$35',
      priceNote: 'per card',
      features: [
        'Better bulk pricing',
        'Advanced team management',
        'Full customization',
        'Priority delivery',
        'Dedicated support',
        'Analytics dashboard',
      ],
      popular: true,
    },
    {
      name: 'Enterprise',
      quantity: '200+ Cards',
      price: 'Custom',
      priceNote: 'pricing',
      features: [
        'Best pricing available',
        'White-label solution',
        'API integration',
        'Express delivery',
        'Account manager',
        'Custom features',
        'SLA guarantee',
      ],
    },
  ];

  const faqs = [
    {
      question: 'What is the minimum order quantity?',
      answer: 'The minimum order for team packages is 10 cards. This ensures you get bulk pricing benefits while equipping your team.',
    },
    {
      question: 'Can we manage all cards from one dashboard?',
      answer: 'Yes! Our team dashboard allows you to create, edit, and manage all your team members cards from one central location.',
    },
    {
      question: 'Do you offer custom branding?',
      answer: 'Absolutely. All team packages include custom branding options to match your company identity, colors, and logo.',
    },
    {
      question: 'What is the delivery time for bulk orders?',
      answer: 'Standard bulk orders take 7-10 business days. Priority and express shipping options are available for faster delivery.',
    },
    {
      question: 'Can we add more cards later?',
      answer: 'Yes, you can easily order additional cards at any time. Your pricing tier is maintained based on total cards ordered.',
    },
    {
      question: 'Is there a setup fee?',
      answer: 'No setup fees! The price per card includes everything - design, NFC programming, and your team dashboard access.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl lg:text-6xl font-extrabold text-navy mb-6">
            Enterprise & Team Solutions
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Equip your entire team with professional digital business cards. Centralized management, bulk pricing, and seamless deployment.
          </p>
          <Button className="bg-navy text-white hover:bg-navy/90 rounded-full px-8 py-6 text-base font-semibold">
            Request a Quote
          </Button>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-extrabold text-navy mb-4">
              Why Teams Choose Smart Connections
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Professional networking solution built for modern teams
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-purple-custom rounded-xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-navy" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3">Centralized Management</h3>
              <p className="text-gray-600">Manage your entire team's cards from one dashboard</p>
            </div>

            <div className="bg-white rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-yellow-custom rounded-xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-navy" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3">Brand Consistency</h3>
              <p className="text-gray-600">Ensure uniform branding across all team members</p>
            </div>

            <div className="bg-white rounded-2xl p-8 text-center">
              <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'hsl(220, 40%, 88%)' }}>
                <TrendingUp className="w-8 h-8 text-navy" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3">Bulk Savings</h3>
              <p className="text-gray-600">Save up to 40% with volume pricing</p>
            </div>

            <div className="bg-white rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-pink-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-navy" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3">Dedicated Support</h3>
              <p className="text-gray-600">Priority support and account management</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Packages */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-extrabold text-navy mb-4">
              Choose Your Team Package
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Flexible pricing designed to scale with your business
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <div
                key={index}
                className={`bg-gray-50 rounded-[2.5rem] p-8 relative ${
                  pkg.popular ? 'ring-4 ring-navy' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-navy text-white text-sm font-bold px-4 py-2 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-navy mb-2">{pkg.name}</h3>
                  <p className="text-gray-600 mb-4">{pkg.quantity}</p>
                  <div className="text-4xl font-extrabold text-navy mb-1">{pkg.price}</div>
                  <p className="text-sm text-gray-600">{pkg.priceNote}</p>
                </div>

                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-navy flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className={`w-full rounded-full py-6 text-base font-semibold ${
                    pkg.popular
                      ? 'bg-navy text-white hover:bg-navy/90'
                      : 'bg-white text-navy border-2 border-navy hover:bg-navy hover:text-white'
                  }`}
                >
                  Get Started
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-extrabold text-navy mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Common questions about enterprise and team solutions
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {faqs.map((faq, index) => (
              <div key={index} className="border-b border-gray-200 pb-4">
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full flex items-center justify-between text-left py-4"
                >
                  <span className="text-lg font-semibold text-navy pr-4">
                    {faq.question}
                  </span>
                  <Plus
                    className={`w-6 h-6 text-navy flex-shrink-0 transition-transform ${
                      openFaqIndex === index ? 'rotate-45' : ''
                    }`}
                  />
                </button>
                {openFaqIndex === index && (
                  <p className="text-gray-600 pb-4">{faq.answer}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
